import {
  HEARTBEAT,
  heartbeatCronLine,
  heartbeatUrl,
  incidentPath,
  MONITOR_SIMULATION_NOTE,
  MONITOR_TCP,
  type ServerMonitorItem,
  type ServerMonitoringDto,
} from '@fos/shared/contracts/monitoring';
import { MONITOR_STATE_LABELS, MONITOR_STATE_TONES, uptimeText } from '@fos/shared/labels';
import { Copy, HeartPulse, KeyRound, Network, Plus, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field, Label } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { latencyText, sinceText } from '../monitoring-format';

const REFRESH_MS = 30_000;

/**
 * Sekcja „Monitoring serwera” w karcie zasobu typu Serwer (Fala 10; czeka na projekt w Figmie): kilka monitorów —
 * porty TCP (np. SSH 22, HTTPS 443) i meldunek serwera (heartbeat: tajny adres, który serwer wywołuje z crona; token
 * pokazany raz z gotową linią crona do skopiowania, rotacja = nowy adres). Status zasobu: „Alarm”, gdy którykolwiek
 * monitor ma awarię.
 */
export function ServerMonitorsCard({ assetId }: { assetId: string }) {
  const utils = trpc.useUtils();
  const query = trpc.monitoring.serverMonitoring.useQuery({ assetId }, { refetchInterval: REFRESH_MS });
  const [dialog, setDialog] = useState<
    | { kind: 'tcp' }
    | { kind: 'heartbeat' }
    | { kind: 'token'; token: string; name: string }
    | { kind: 'settings'; item: ServerMonitorItem }
    | null
  >(null);
  const checkNow = trpc.monitoring.checkNow.useMutation();
  const rotate = trpc.monitoring.rotateHeartbeatToken.useMutation();
  const update = trpc.monitoring.updateMonitor.useMutation();
  const archive = trpc.monitoring.archiveMonitor.useMutation();

  const refresh = () => Promise.all([utils.monitoring.invalidate(), utils.maintenance.invalidate()]);
  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast.success(done);
      await refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (query.isPending) {
    return (
      <Section aria-label="Monitoring serwera" className="md:px-6">
        <div role="status" aria-label="Wczytywanie monitoringu serwera" className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-3/5" />
          <Skeleton className="h-5 w-full" />
        </div>
      </Section>
    );
  }
  if (query.isError) {
    return (
      <Section aria-label="Monitoring serwera" className="md:px-6">
        <ErrorState
          compact
          title="Nie udało się wczytać monitoringu serwera"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </Section>
    );
  }
  const data: ServerMonitoringDto = query.data;
  const busy = checkNow.isPending || rotate.isPending || update.isPending || archive.isPending;

  return (
    <Section aria-label="Monitoring serwera" className="md:px-6">
      <h2 className="text-caption text-fg-muted">Monitoring serwera</h2>
      {data.items.length === 0 ? (
        <p className="mt-2.5 text-caption text-fg-secondary">
          Brak monitorów. Dodaj port (np. SSH 22, HTTPS 443) albo meldunek — serwer co kilka minut zgłasza się sam, a
          cisza oznacza alarm.
        </p>
      ) : (
        <ul className="mt-1.5">
          {data.items.map((item) => (
            <MonitorItem
              key={item.monitor.id}
              item={item}
              canEdit={data.canEdit}
              busy={busy}
              onCheck={() =>
                run(() => checkNow.mutateAsync({ id: item.monitor.id }), 'Sprawdzenie zlecone — wynik w ciągu minuty')
              }
              onRotate={async () => {
                try {
                  const res = await rotate.mutateAsync({ id: item.monitor.id });
                  await refresh();
                  setDialog({ kind: 'token', token: res.token, name: item.monitor.name });
                } catch (err) {
                  toast.error(errorMessage(err));
                }
              }}
              onSettings={() => setDialog({ kind: 'settings', item })}
              onToggle={() =>
                run(
                  () =>
                    update.mutateAsync({
                      id: item.monitor.id,
                      configHash: item.monitor.configHash,
                      enabled: !item.monitor.enabled,
                    }),
                  item.monitor.enabled ? 'Monitor wyłączony' : 'Monitor włączony',
                )
              }
              onArchive={() =>
                run(() => archive.mutateAsync({ id: item.monitor.id }), 'Monitor usunięty (historia zostaje)')
              }
            />
          ))}
        </ul>
      )}
      {data.canEdit && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button
            size="s"
            className={TOUCH_BUTTON}
            variant="secondary"
            icon={Network}
            onClick={() => setDialog({ kind: 'tcp' })}
          >
            Dodaj port
          </Button>
          <Button
            size="s"
            className={TOUCH_BUTTON}
            variant="secondary"
            icon={HeartPulse}
            onClick={() => setDialog({ kind: 'heartbeat' })}
          >
            Dodaj meldunek
          </Button>
        </div>
      )}

      <AddTcpDialog
        open={dialog?.kind === 'tcp'}
        onOpenChange={(open) => !open && setDialog(null)}
        assetId={assetId}
        suggestedHost={data.suggestedHost}
        onSaved={refresh}
      />
      <AddHeartbeatDialog
        open={dialog?.kind === 'heartbeat'}
        onOpenChange={(open) => !open && setDialog(null)}
        assetId={assetId}
        onCreated={async (token, name) => {
          await refresh();
          setDialog({ kind: 'token', token, name });
        }}
      />
      <Dialog
        open={dialog?.kind === 'token'}
        onOpenChange={(open) => !open && setDialog(null)}
        width="l"
        title="Adres meldunku serwera"
        description={dialog?.kind === 'token' ? dialog.name : undefined}
      >
        {dialog?.kind === 'token' && <HeartbeatTokenView token={dialog.token} onDone={() => setDialog(null)} />}
      </Dialog>
      <HeartbeatSettingsDialog
        item={dialog?.kind === 'settings' ? dialog.item : null}
        onOpenChange={(open) => !open && setDialog(null)}
        onSaved={refresh}
      />
    </Section>
  );
}

function kindText(item: ServerMonitorItem): string {
  const m = item.monitor;
  if (m.kind === 'tcp' && m.target) return `Port ${m.target.port} · ${m.target.host}`;
  if (m.kind === 'heartbeat' && item.heartbeat) return `Meldunek · alarm po ${item.heartbeat.maxSilenceMin} min ciszy`;
  return m.url;
}

function metricsText(item: ServerMonitorItem): string | null {
  const metrics = item.heartbeat?.lastMetrics;
  if (!metrics) return null;
  const parts: string[] = [];
  if (metrics.diskUsedPercent !== null) parts.push(`dysk ${String(metrics.diskUsedPercent).replace('.', ',')}%`);
  if (metrics.load1 !== null) parts.push(`obciążenie ${String(metrics.load1).replace('.', ',')}`);
  return parts.join(' · ') || null;
}

function MonitorItem({
  item,
  canEdit,
  busy,
  onCheck,
  onRotate,
  onSettings,
  onToggle,
  onArchive,
}: {
  item: ServerMonitorItem;
  canEdit: boolean;
  busy: boolean;
  onCheck: () => void;
  onRotate: () => void;
  onSettings: () => void;
  onToggle: () => void;
  onArchive: () => void;
}) {
  const m = item.monitor;
  const heartbeat = m.kind === 'heartbeat';
  const last = heartbeat ? item.heartbeat?.lastBeatAt : item.lastCheck?.checkedAt;
  const metrics = metricsText(item);
  return (
    <li className="border-b border-line py-2.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="min-w-0 flex-1 text-body-s font-medium break-words text-fg">{m.name}</span>
        {m.enabled ? (
          <StatusPill tone={MONITOR_STATE_TONES[m.state]}>{MONITOR_STATE_LABELS[m.state]}</StatusPill>
        ) : (
          <StatusPill tone="neutral">Wyłączony</StatusPill>
        )}
      </div>
      <p className="mt-0.5 text-caption break-words text-fg-muted">
        {kindText(item)}
        {m.mode === 'simulated' ? ` · Symulacja (${MONITOR_SIMULATION_NOTE.toLowerCase()})` : ''}
      </p>
      <p className="mt-0.5 text-caption text-fg-secondary">
        {heartbeat ? 'Ostatni meldunek: ' : 'Ostatnie sprawdzenie: '}
        {last ? formatRelativePast(last) : heartbeat ? 'jeszcze nie było' : 'jeszcze nie sprawdzono'}
        {!heartbeat && item.lastCheck?.ok && latencyText(item.lastCheck.latencyMs) && (
          <>
            {' · '}
            <Mono>{latencyText(item.lastCheck.latencyMs)}</Mono>
          </>
        )}
        {metrics ? ` · ${metrics}` : ''}
        {` · dostępność 30 dni: ${uptimeText(item.uptimePercent)}`}
      </p>
      {item.openIncident ? (
        <Link
          to={incidentPath(item.openIncident.id)}
          className="mt-1.5 block rounded-lg bg-danger-subtle px-3 py-1.5 text-caption text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {item.openIncident.title} {sinceText(item.openIncident.openedAt)}
          {item.openIncident.cause ? ` · ${item.openIncident.cause}` : ''}
        </Link>
      ) : (
        m.lastError && m.enabled && <p className="mt-0.5 text-caption break-words text-danger">{m.lastError}</p>
      )}
      {canEdit && (
        <div className="mt-2 flex flex-wrap gap-1">
          {m.enabled && (
            <Button
              size="s"
              className={TOUCH_BUTTON}
              variant="ghost"
              icon={RefreshCw}
              disabled={busy}
              onClick={onCheck}
            >
              Sprawdź teraz
            </Button>
          )}
          {heartbeat && (
            <>
              <Button
                size="s"
                className={TOUCH_BUTTON}
                variant="ghost"
                icon={SlidersHorizontal}
                disabled={busy}
                onClick={onSettings}
              >
                Progi
              </Button>
              <Button
                size="s"
                className={TOUCH_BUTTON}
                variant="ghost"
                icon={KeyRound}
                disabled={busy}
                onClick={onRotate}
              >
                Nowy adres
              </Button>
            </>
          )}
          <Button size="s" className={TOUCH_BUTTON} variant="ghost" disabled={busy} onClick={onToggle}>
            {m.enabled ? 'Wyłącz' : 'Włącz'}
          </Button>
          <Button size="s" className={TOUCH_BUTTON} variant="ghost" disabled={busy} onClick={onArchive}>
            Usuń
          </Button>
        </div>
      )}
    </li>
  );
}

async function copyText(text: string, done: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(done);
  } catch {
    toast.error('Nie udało się skopiować. Zaznacz tekst i skopiuj ręcznie.');
  }
}

/** Adres meldunku i gotowe linie crona (token pokazany raz). Adres = adres tej aplikacji + `/api/heartbeat/…`. */
export function HeartbeatTokenView({ token, onDone }: { token: string; onDone: () => void }) {
  const url = heartbeatUrl(window.location.origin, token);
  const cron = heartbeatCronLine(url);
  const simple = heartbeatCronLine(url, { simple: true });
  const blocks: Array<{ label: string; value: string; copied: string }> = [
    { label: 'Adres meldunku (tajny)', value: url, copied: 'Adres skopiowany' },
    { label: 'Linia crona (co 5 min, z dyskiem i obciążeniem)', value: cron, copied: 'Linia crona skopiowana' },
    { label: 'Albo sam meldunek, bez metryk', value: simple, copied: 'Linia crona skopiowana' },
  ];
  return (
    <div className="flex flex-col gap-5">
      <ContextBanner tone="warning">
        Ten adres zobaczysz tylko teraz — działa jak hasło. Skopiuj go do crona serwera (crontab -e). Nowy adres
        unieważnia stary.
      </ContextBanner>
      {blocks.map((b) => (
        <div key={b.label} className="flex flex-col gap-2">
          <Label>{b.label}</Label>
          <div className="flex items-start gap-2">
            <Mono
              aria-label={b.label}
              className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-2 text-caption break-all text-fg select-all"
            >
              {b.value}
            </Mono>
            <Button
              variant="secondary"
              icon={Copy}
              aria-label={`Kopiuj: ${b.label}`}
              onClick={() => void copyText(b.value, b.copied)}
            />
          </div>
        </div>
      ))}
      <p className="text-caption text-fg-muted">
        Serwer musi widzieć ten adres z internetu. Meldunek bez metryk też się liczy; zajętość dysku i obciążenie (load
        average z 1 min) porównujemy z progami monitora.
      </p>
      <div className="flex justify-end">
        <Button variant="primary" onClick={onDone}>
          Gotowe
        </Button>
      </div>
    </div>
  );
}

function AddTcpDialog({
  open,
  onOpenChange,
  assetId,
  suggestedHost,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  suggestedHost: string | null;
  onSaved: () => Promise<unknown>;
}) {
  const create = trpc.monitoring.createServerMonitor.useMutation();
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setHost(suggestedHost ?? '');
    setPort('22');
    setErrors({});
    setFormError(null);
  }, [open, suggestedHost]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const p = Number(port);
    const next: Record<string, string> = {};
    if (!host.trim()) next.host = 'Podaj nazwę serwera albo adres IP';
    if (!Number.isInteger(p) || p < 1 || p > 65_535) next.port = 'Port od 1 do 65535';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      await create.mutateAsync({ kind: 'tcp', assetId, host: host.trim(), port: p });
      toast.success('Port dodany', { description: 'Pierwsze sprawdzenie w ciągu minuty.' });
      await onSaved();
      onOpenChange(false);
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return;
      const mapped: Record<string, string> = {};
      for (const issue of validationIssues(err)) mapped[issue.path] ??= issue.message;
      if (mapped.host || mapped.port) setErrors(mapped);
      else setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Dodaj port"
      description="Sprawdzamy, czy port przyjmuje połączenia (np. SSH 22, HTTPS 443). Tylko publiczne adresy."
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Nowy monitor portu">
        <Field label="Serwer (nazwa albo IP)" error={errors.host}>
          <Input autoFocus value={host} onChange={(e) => setHost(e.target.value)} placeholder="serwer.fravity.pl" />
        </Field>
        <Field label="Port" error={errors.port}>
          <Input
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => setPort(e.target.value)}
            className="w-28"
          />
        </Field>
        <div className="-mt-2 flex flex-wrap gap-1.5">
          {MONITOR_TCP.suggestedPorts.slice(0, 6).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPort(String(p))}
              className="rounded-md border border-line bg-canvas px-2 py-0.5 font-mono text-caption text-fg-secondary hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent"
            >
              {p}
            </button>
          ))}
        </div>
        {formError && <FormMessage>{formError}</FormMessage>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
            Dodaj
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function numOrNull(v: string): number | null {
  const t = v.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : Number.NaN;
}

function HeartbeatFields({
  silence,
  disk,
  load,
  setSilence,
  setDisk,
  setLoad,
  errors,
}: {
  silence: string;
  disk: string;
  load: string;
  setSilence: (v: string) => void;
  setDisk: (v: string) => void;
  setLoad: (v: string) => void;
  errors: Record<string, string>;
}) {
  return (
    <>
      <Field
        label="Alarm po ciszy dłuższej niż (min)"
        hint="Przy cronie co 5 min — np. 10 min"
        error={errors.maxSilenceMin}
      >
        <Input
          type="number"
          min={HEARTBEAT.maxSilenceMin.min}
          value={silence}
          onChange={(e) => setSilence(e.target.value)}
          className="w-28"
        />
      </Field>
      <Field label="Próg zajętości dysku (%)" hint="Puste = bez progu" error={errors.diskMaxPercent}>
        <Input
          type="number"
          min={1}
          max={100}
          value={disk}
          onChange={(e) => setDisk(e.target.value)}
          className="w-28"
        />
      </Field>
      <Field
        label="Próg obciążenia (load z 1 min)"
        hint="Puste = bez progu, np. 4 dla 4 rdzeni"
        error={errors.load1Max}
      >
        <Input inputMode="decimal" value={load} onChange={(e) => setLoad(e.target.value)} className="w-28" />
      </Field>
    </>
  );
}

function parseHeartbeat(silence: string, disk: string, load: string) {
  const errors: Record<string, string> = {};
  const s = Number(silence);
  if (!Number.isInteger(s) || s < HEARTBEAT.maxSilenceMin.min || s > HEARTBEAT.maxSilenceMin.max) {
    errors.maxSilenceMin = `Od ${HEARTBEAT.maxSilenceMin.min} min do 7 dni`;
  }
  const d = numOrNull(disk);
  if (d !== null && (!Number.isInteger(d) || d < 1 || d > 100)) errors.diskMaxPercent = 'Od 1 do 100';
  const l = numOrNull(load);
  if (l !== null && (Number.isNaN(l) || l <= 0)) errors.load1Max = 'Liczba większa od zera';
  return { errors, values: { maxSilenceMin: s, diskMaxPercent: d, load1Max: l } };
}

function AddHeartbeatDialog({
  open,
  onOpenChange,
  assetId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  onCreated: (token: string, name: string) => Promise<void>;
}) {
  const create = trpc.monitoring.createServerMonitor.useMutation();
  const [silence, setSilence] = useState(String(HEARTBEAT.maxSilenceMin.default));
  const [disk, setDisk] = useState('90');
  const [load, setLoad] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSilence(String(HEARTBEAT.maxSilenceMin.default));
    setDisk('90');
    setLoad('');
    setErrors({});
    setFormError(null);
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const parsed = parseHeartbeat(silence, disk, load);
    setErrors(parsed.errors);
    if (Object.keys(parsed.errors).length > 0) return;
    try {
      const res = await create.mutateAsync({ kind: 'heartbeat', assetId, ...parsed.values });
      onOpenChange(false);
      await onCreated(res.token as string, res.monitor.name);
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return;
      setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Dodaj meldunek serwera"
      description="Serwer co kilka minut wywołuje tajny adres (cron + curl). Brak meldunku albo przekroczony próg = alarm."
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Nowy meldunek serwera">
        <HeartbeatFields
          silence={silence}
          disk={disk}
          load={load}
          setSilence={setSilence}
          setDisk={setDisk}
          setLoad={setLoad}
          errors={errors}
        />
        {formError && <FormMessage>{formError}</FormMessage>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Utwórz adres meldunku
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function HeartbeatSettingsDialog({
  item,
  onOpenChange,
  onSaved,
}: {
  item: ServerMonitorItem | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<unknown>;
}) {
  const save = trpc.monitoring.updateHeartbeatSettings.useMutation();
  const [silence, setSilence] = useState('');
  const [disk, setDisk] = useState('');
  const [load, setLoad] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const hb = item?.heartbeat ?? null;

  useEffect(() => {
    if (!hb) return;
    setSilence(String(hb.maxSilenceMin));
    setDisk(hb.diskMaxPercent === null ? '' : String(hb.diskMaxPercent));
    setLoad(hb.load1Max === null ? '' : String(hb.load1Max).replace('.', ','));
    setErrors({});
  }, [hb]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!item) return;
    const parsed = parseHeartbeat(silence, disk, load);
    setErrors(parsed.errors);
    if (Object.keys(parsed.errors).length > 0) return;
    try {
      await save.mutateAsync({ id: item.monitor.id, ...parsed.values });
      toast.success('Progi zapisane');
      await onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange} title="Progi meldunku" description={item?.monitor.name}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Progi meldunku">
        <HeartbeatFields
          silence={silence}
          disk={disk}
          load={load}
          setSilence={setSilence}
          setDisk={setDisk}
          setLoad={setLoad}
          errors={errors}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Zapisz
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
